// Dropping a PDF on a page with no drop handler doesn't fail quietly — the
// browser navigates to the file, which is why "drag and drop the invoice" looked
// like it "just displays the pdf in another tab". Every invoice upload surface
// shares these helpers so the drop is caught, the right file is picked out, and
// the same validation message comes back regardless of how the file arrived.

export const MAX_INVOICE_BYTES = 20 * 1024 * 1024;

const isPdf = (file: File): boolean =>
  file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

/**
 * The PDF out of a drop, or the first file so a wrong-type drop still produces a
 * validation error rather than silence. Falls back to `items` because some
 * browsers leave `files` empty on drops that originate outside the browser.
 */
export const pickDroppedPdf = (data: DataTransfer | null | undefined): File | null => {
  if (!data) return null;

  const fromFiles = Array.from(data.files ?? []);
  if (fromFiles.length) return fromFiles.find(isPdf) ?? fromFiles[0];

  const fromItems = Array.from(data.items ?? [])
    .filter((item) => item.kind === 'file')
    .map((item) => item.getAsFile())
    .filter((file): file is File => !!file);
  if (fromItems.length) return fromItems.find(isPdf) ?? fromItems[0];

  return null;
};

export const isZip = (file: File): boolean =>
  /zip/i.test(file.type) || file.name.toLowerCase().endsWith('.zip');

/** Most PDFs one zip may add to the inbox at once. */
export const MAX_ZIP_PDFS = 50;

/**
 * The PDFs inside a zip as Files, ready for the same upload as a dropped PDF.
 * Folders and Mac resource forks are ignored; anything else that is not a PDF
 * comes back in `skipped` so the person can see what was left out.
 */
export const unzipPdfs = async (zipFile: File): Promise<{ pdfs: File[]; skipped: string[] }> => {
  const { default: JSZip } = await import('jszip');
  const zip = await JSZip.loadAsync(zipFile);
  const pdfs: File[] = [];
  const skipped: string[] = [];
  for (const entry of Object.values(zip.files)) {
    const base = entry.name.split('/').pop() ?? entry.name;
    if (entry.dir || entry.name.startsWith('__MACOSX/') || base.startsWith('._')) continue;
    if (!base.toLowerCase().endsWith('.pdf') || pdfs.length >= MAX_ZIP_PDFS) { skipped.push(base); continue; }
    pdfs.push(new File([await entry.async('blob')], base, { type: 'application/pdf' }));
  }
  return { pdfs, skipped };
};

/**
 * Every file in a drop, for surfaces that take several at once (the inbox).
 * Wrong-type files are kept so each can be reported, not silently skipped.
 */
export const pickDroppedFiles = (data: DataTransfer | null | undefined): File[] => {
  if (!data) return [];
  const fromFiles = Array.from(data.files ?? []);
  if (fromFiles.length) return fromFiles;
  return Array.from(data.items ?? [])
    .filter((item) => item.kind === 'file')
    .map((item) => item.getAsFile())
    .filter((file): file is File => !!file);
};

/** Error message to show the user, or null when the file is a usable invoice. */
export const validateInvoicePdf = (file: File | null | undefined): string | null => {
  if (!file) return 'No file was dropped';
  if (!isPdf(file)) return 'Only PDF files are supported';
  if (file.size > MAX_INVOICE_BYTES) return 'File must be under 20 MB';
  return null;
};
